import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaEdit, FaUser, FaReceipt, FaMoneyBillWave } from 'react-icons/fa';
import { LuUserCog } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'account-settings';

const SUB_PAGES = [
  {
    slug: 'account-profile',
    route: '/portal-cms/account-settings/profile',
    label: 'My Profile',
    description: 'Profile page header, sidebar label, icon, and Help Guide card',
    icon: FaUser,
    gradient: 'from-indigo-500 to-violet-600',
  },
  {
    slug: 'account-invoice',
    route: '/portal-cms/account-settings/invoice',
    label: 'Invoice Download',
    description: 'Invoice page header, notice banner, sidebar label, and Help card',
    icon: FaReceipt,
    gradient: 'from-cyan-500 to-teal-600',
  },
  {
    slug: 'account-payment-history',
    route: '/portal-cms/account-settings/payment-history',
    label: 'Payment History',
    description: 'Payment history page header, sidebar label, and Help card',
    icon: FaMoneyBillWave,
    gradient: 'from-emerald-500 to-green-600',
  },
];

const defaultForm = () => ({
  isPublished: true,
  title: 'Account Settings',
  icon: 'LuSettings',
  intro: '',
});

const AccountSettingsPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          isPublished: res.data.data.isPublished !== false,
          title: c.title || '',
          icon: c.icon || 'LuSettings',
          intro: c.intro || '',
        });
      }
    } catch {
      toastError('Failed to load account settings content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Account settings saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded defaults for Account Settings section?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Account Settings"
        description="Edit the sidebar group label and manage My Profile, Invoice Download, and Payment History."
        icon={LuUserCog}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">
              Back
            </button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> {resetting ? 'Resetting...' : 'Reset Section'}
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Section'}
            </button>
          </div>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4 mb-8">
        <h2 className="font-bold text-gray-900 dark:text-white">Sidebar & Help Guide section</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Controls the Account Settings dropdown label in the sidebar and the intro on the Help page.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          Published
        </label>
        <input className="input-modern" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Sidebar dropdown title" />
        <PortalIconPicker value={form.icon} onChange={(icon) => setForm((prev) => ({ ...prev, icon }))} />
        <textarea className="input-modern min-h-[100px]" value={form.intro} onChange={(e) => setForm((prev) => ({ ...prev, intro: e.target.value }))} placeholder="Help Guide account section intro" />
      </div>

      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Account pages</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Click a page below to edit its title, subtitle, icon, and content.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {SUB_PAGES.map((page) => {
          const Icon = page.icon;
          return (
            <div
              key={page.slug}
              className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card overflow-hidden flex flex-col"
            >
              <div className={`h-2 bg-gradient-to-r ${page.gradient}`} />
              <div className="p-5 flex-1 flex flex-col">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${page.gradient} flex items-center justify-center text-white shadow-lg mb-4`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{page.label}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex-1">{page.description}</p>
                <button
                  type="button"
                  onClick={() => navigate(page.route)}
                  className="btn-premium text-sm py-2 px-4 flex items-center justify-center gap-2 w-full"
                >
                  <FaEdit className="w-3.5 h-3.5" /> Edit Page
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </PageLayout>
  );
};

export default AccountSettingsPortalCMS;
